import { buildCorpus } from "./corpus.js";

/*
 * Instant, offline matching of a question to a tutorial step, so the page can
 * show the right screenshot the moment a question is asked — before (and
 * without) the AI answer from POST /tutorial/ask.
 *
 * BM25 with the SAME tokenizer as the server's ranking
 * (backend-nextja/src/server/tutorialAsk.ts). Keep the two in step: if they
 * disagree, the screenshot shown first and the one the answer cites drift
 * apart.
 */
const STOPWORDS = new Set(
  "a an and are as at be by can do does for from get how i if in is it me my of on or so that the this to use using what when where which with you your toplistbot".split(
    " "
  )
);

export function tokenize(text) {
  const out = [];
  for (const raw of text.toLowerCase().match(/[a-z0-9]+/g) ?? []) {
    if (raw.length < 2 || STOPWORDS.has(raw)) continue;
    out.push(raw);
    // Crude stemming so "votes"/"vote", "paused"/"pause", "tokens"/"token" meet.
    const stem = raw.replace(/(ing|ed|es|s)$/, "");
    if (stem !== raw && stem.length > 2) out.push(stem);
  }
  return out;
}

export const entries = buildCorpus();

const index = (() => {
  const docs = entries.map((e) => {
    // Titles say what a step is about; they count several times over the body.
    const terms = [
      ...Array(3).fill(tokenize(e.title)).flat(),
      ...Array(2).fill(tokenize(e.sectionTitle)).flat(),
      ...tokenize(e.text),
    ];
    const tf = new Map();
    for (const t of terms) tf.set(t, (tf.get(t) ?? 0) + 1);
    return { tf, len: terms.length };
  });
  const df = new Map();
  for (const d of docs) d.tf.forEach((_, t) => df.set(t, (df.get(t) ?? 0) + 1));
  const avgLen = docs.reduce((n, d) => n + d.len, 0) / Math.max(1, docs.length);
  return { docs, df, avgLen };
})();

/** Steps ranked for `question`, best first; empty when no word matched. */
export function searchSteps(question, limit = 3) {
  const terms = Array.from(new Set(tokenize(question)));
  const { docs, df, avgLen } = index;
  const N = docs.length;
  const k1 = 1.2;
  const b = 0.75;
  return docs
    .map((d, i) => {
      let s = 0;
      for (const t of terms) {
        const f = d.tf.get(t);
        if (!f) continue;
        const n = df.get(t) ?? 0;
        const idf = Math.log(1 + (N - n + 0.5) / (n + 0.5));
        s += idf * ((f * (k1 + 1)) / (f + k1 * (1 - b + (b * d.len) / avgLen)));
      }
      return { entry: entries[i], score: s };
    })
    .filter((r) => r.score > 0)
    .sort((x, y) => y.score - x.score)
    .slice(0, limit)
    .map((r) => r.entry);
}

export const entryById = (id) => entries.find((e) => e.id === id) || null;
